import '../menu/menu.scss';
import './buttons-v1-1.scss';

import React, { PropsWithChildren, useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import { usePopper } from 'react-popper';

import { ObjectLiteral } from '../../core';
import { ChevronDownSVGIcon } from '../svg/svg';
import { ItemProps } from './menuItems';
import { useOutsideClick, useDelayedUnmount } from '../hooks';

export interface IFilterableSource {
  getFilterFieldValue(field: string): unknown;

  setFilterFieldValue(field: string, val?: unknown): void;
}

type Props = PropsWithChildren<{
  className?: string | ObjectLiteral;
  label?: string;
  selected?: boolean;
  items?: ItemProps[];
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
}>;

export const FilterFieldButton = observer((props: Props) => {
  const [isOpened, setIsOpened] = useState(false);
  const { shouldRender, isAnimating } = useDelayedUnmount(isOpened, 400);

  // Listen for custom event to close this dropdown when another opens
  useEffect(() => {
    const handleCloseAll = () => {
      if (isOpened) {
        setIsOpened(false);
      }
    };
    document.addEventListener('dropdown:close-all', handleCloseAll);
    return () => document.removeEventListener('dropdown:close-all', handleCloseAll);
  }, [isOpened]);

  const className = classNames(
    'sort-field-btn btn-core btn sort filter',
    props.className,
    {
      'selected': props.selected || false,
      'open active': isOpened,
    }
  );
  const [popperElement, setPopperElement] = useState(null);
  const [anchorElement, setAnchorElement] = useState(null);
  const [arrowElement] = useState(null);

  const { styles, attributes } = usePopper(anchorElement, popperElement, {
    placement: 'bottom-end',
    strategy: 'fixed',
    modifiers: [
      {
        name: 'arrow',
        options: {
          element: arrowElement,
        },
      },

      {
        name: 'offset',
        options: {
          offset: [0, 6],
        },
      },

      {
        name: 'flip',
        options: {
          fallbackPlacements: ['left', 'top', 'bottom'],
        },
      },
    ],
  });

  useOutsideClick(popperElement, () => setIsOpened(false));

  return (
    <div className="flex-center">
      <button
        disabled={props.disabled}
        className={className}
        ref={setAnchorElement as React.Ref<HTMLButtonElement>}
        onClick={(evt) => {
          // Close other dropdowns/menus before opening this one
          if (!isOpened) {
            document.dispatchEvent(new CustomEvent('dropdown:close-all'));
          }
          setIsOpened(!isOpened);
          evt.stopPropagation();
        }}>
        <span className="text">{props.label}</span>

        <span className="icon">
          <ChevronDownSVGIcon />
        </span>
      </button>

      {shouldRender && (
        <div
          className={classNames('dropdown-menu menu-popup popup menu-animate', isAnimating)}
          ref={setPopperElement as React.Ref<HTMLDivElement>}
          style={styles.popper}
          {...attributes.popper}>
          <div className="popup-arrow" style={styles.arrow} />
          <div className="menu-items" onClick={(_evt) => setIsOpened(false)}>
            {props.children}
          </div>
        </div>
      )}
    </div>
  );
});
